'use client';

import * as React from 'react';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon, CopyIcon } from 'lucide-react';

import { Button } from '@workspace/ui/components/button';
import { cn } from '@workspace/ui/lib/utils';

interface ExpandablePromptProps {
    children: React.ReactNode;
    title?: string;
    previewLines?: number;
}

export function ExpandablePrompt({
    children,
    title = 'Prompt to Copy:',
    previewLines = 4,
}: ExpandablePromptProps): React.JSX.Element {
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [copied, setCopied] = React.useState(false);
    const [textContent, setTextContent] = React.useState('');
    const [previewContent, setPreviewContent] = React.useState('');
    const contentRef = React.useRef<HTMLDivElement>(null);

    // Extract text content from children on mount and when children change
    React.useEffect(() => {
        if (contentRef.current) {
            const text = contentRef.current.textContent || '';
            setTextContent(text);

            const lines = text.split('\n');
            const preview = lines.slice(0, previewLines).join('\n');
            setPreviewContent(preview);
        }
    }, [children, previewLines]);

    const handleCopy = React.useCallback(() => {
        navigator.clipboard.writeText(textContent).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    }, [textContent]);

    const handleToggle = React.useCallback(() => {
        setIsExpanded((prev) => !prev);
    }, []);

    const handleKeyDown = React.useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleToggle();
            }
        },
        [handleToggle]
    );

    const totalLines = textContent.split('\n').length;
    const hasMoreContent = totalLines > previewLines;

    return (
        <div className="my-6 rounded-lg border border-border bg-card">
            {/* Header with title and copy button */}
            <div className="flex items-center justify-between gap-2 border-b border-border bg-muted/50 px-4 py-2.5">
                <span className="text-sm font-medium text-muted-foreground">{title}</span>
                <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 px-3 hover:bg-background/80"
                    onClick={handleCopy}
                    type="button"
                    aria-label="Copy prompt to clipboard"
                >
                    {copied ? (
                        <>
                            <CheckIcon className="mr-1.5 h-3.5 w-3.5" />
                            <span className="text-xs">Copied</span>
                        </>
                    ) : (
                        <>
                            <CopyIcon className="mr-1.5 h-3.5 w-3.5" />
                            <span className="text-xs">Copy</span>
                        </>
                    )}
                </Button>
            </div>

            {/* Hidden content for text extraction */}
            <div ref={contentRef} className="sr-only">
                {children}
            </div>

            {/* Visible content area */}
            <div className="relative bg-muted/30">
                <div
                    className={cn(
                        'overflow-hidden px-4 py-3 font-mono text-sm leading-relaxed transition-all duration-200',
                        !isExpanded && hasMoreContent && 'max-h-[9rem]'
                    )}
                >
                    <div className="whitespace-pre-wrap break-words text-foreground">
                        {isExpanded ? textContent : previewContent}
                        {!isExpanded && hasMoreContent && (
                            <span className="text-muted-foreground"> ...</span>
                        )}
                    </div>
                </div>

                {/* Gradient fade for collapsed state */}
                {!isExpanded && hasMoreContent && (
                    <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-card to-transparent" />
                )}
            </div>

            {/* Expand/Collapse button */}
            {hasMoreContent && (
                <div className="border-t border-border bg-card px-4 py-2">
                    <button
                        type="button"
                        className="flex w-full items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-primary transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        onClick={handleToggle}
                        onKeyDown={handleKeyDown}
                        aria-expanded={isExpanded}
                        aria-label={isExpanded ? 'Collapse prompt' : 'Expand prompt'}
                    >
                        {isExpanded ? (
                            <>
                                <ChevronUpIcon className="h-4 w-4" />
                                <span>Collapse</span>
                            </>
                        ) : (
                            <>
                                <ChevronDownIcon className="h-4 w-4" />
                                <span>Expand</span>
                            </>
                        )}
                    </button>
                </div>
            )}
        </div>
    );
}

